// Finding the API from the discovery document (servers-and-developers WEB-SRV-BOOT-01 … 05; VisionPlatform regions
// REG-20, REG-22, REG-23, REG-25, app-server-selection SRV-07, SRV-08). Read once at start, before the app renders:
// global routes go to `global_api`, an organisation's routes to its region's `api`, and the app moves to the region's
// `web` when that is another origin. Outside React, like the configuration, so the API client can read it.
import type { AppEnvironment } from "../config";
import builtInDev from "./builtin/dev.json";
import builtInProduction from "./builtin/production.json";
import type { CreavisionRegionsDiscoveryDocument, Region } from "./generated/regions";
import { REGIONS_PATH } from "./regions";

/** What the API routing needs of a region. */
export type ApiRegion = Pick<Region, "id" | "status" | "api" | "web">;
export type Discovery = { globalApi: string; regions: ApiRegion[] };

/** The copy built into the app for each environment (REG-20); staging has none and keeps `apiBase`. */
const BUILT_IN: Partial<Record<AppEnvironment, unknown>> = { production: builtInProduction, dev: builtInDev };

/**
 * An origin the document may name (WEB-SRV-BOOT-05, as WEB-SRV-LST-03): `https://`, no path, user or port, and a host
 * that is `creavision.tech` or ends in `.creavision.tech`. Returns the origin, or undefined.
 */
export function creavisionOrigin(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  let u: URL;
  try {
    u = new URL(value);
  } catch {
    return undefined;
  }
  if (u.protocol !== "https:" || u.username || u.password || u.port) return undefined;
  if (u.pathname !== "/" || u.search || u.hash || value.replace(/\/$/, "") !== u.origin) return undefined;
  const host = u.hostname;
  if (host !== "creavision.tech" && !host.endsWith(".creavision.tech")) return undefined;
  return u.origin;
}

/** The document's routing part, or undefined when it does not validate: one bad origin refuses the whole document. */
export function parseDiscovery(raw: unknown): Discovery | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const doc = raw as Partial<CreavisionRegionsDiscoveryDocument>;
  if (doc.schema !== 1 || !Array.isArray(doc.regions) || doc.regions.length === 0) return undefined;
  const globalApi = creavisionOrigin(doc.global_api);
  if (!globalApi) return undefined;
  const regions: ApiRegion[] = [];
  for (const r of doc.regions as unknown[]) {
    if (typeof r !== "object" || r === null) return undefined;
    const o = r as Record<string, unknown>;
    const api = creavisionOrigin(o.api);
    const web = creavisionOrigin(o.web);
    if (typeof o.id !== "string" || !/^[a-z]{2}[0-9]$/.test(o.id)) return undefined;
    if (!["open", "closed", "retiring"].includes(o.status as string) || !api || !web) return undefined;
    regions.push({ id: o.id, status: o.status as Region["status"], api, web });
  }
  return { globalApi, regions };
}

let discovery: Discovery | undefined;
let origin = "";

/**
 * Reads this server's document from the app's own origin; when it cannot be read or does not validate, the copy built
 * in for `environment`, without a word to the person (WEB-SRV-BOOT-01). Never throws.
 *
 * The document is used only when this page is served from one of its regions' `web` origins: a page served from
 * anywhere else (a build on this machine, the e2e suite's localhost) keeps `config.json` `apiBase`, since its API is
 * not the one the document names.
 */
export async function loadDiscovery(
  environment: AppEnvironment,
  pageOrigin: string = window.location.origin,
  fetchImpl: typeof fetch = fetch,
): Promise<Discovery | undefined> {
  origin = pageOrigin;
  let found: Discovery | undefined;
  try {
    const res = await fetchImpl(REGIONS_PATH, { cache: "no-cache" });
    if (res.ok) found = parseDiscovery(await res.json());
  } catch {
    // Not served or not JSON: the built-in copy below.
  }
  found ??= parseDiscovery(BUILT_IN[environment]);
  discovery = found && found.regions.some((r) => r.web === pageOrigin) ? found : undefined;
  return discovery;
}

export function setDiscoveryForTests(d: Discovery | undefined, pageOrigin = "https://iot.creavision.tech"): void {
  discovery = d;
  origin = pageOrigin;
  regionByOrg.clear();
}

// Each organisation's region, from the memberships of `GET /v1/me` (REG-23).
const regionByOrg = new Map<string, string | undefined>();

export function noteMemberships(memberships: readonly { org_id: string; data_region?: string }[]): void {
  regionByOrg.clear();
  for (const m of memberships) regionByOrg.set(m.org_id, m.data_region);
}

/**
 * The organisation's region: the one its `data_region` names; without one, the one open region; with none or several
 * open, or an id the document does not list, none (WEB-SRV-BOOT-03).
 */
export function regionOfOrg(orgId: string | undefined): ApiRegion | undefined {
  if (!discovery || !orgId) return undefined;
  const id = regionByOrg.get(orgId);
  if (id) return discovery.regions.find((r) => r.id === id);
  const open = discovery.regions.filter((r) => r.status === "open");
  return open.length === 1 ? open[0] : undefined;
}

/** Same-origin bases are given as "" so requests stay relative, as with `apiBase` "" today. */
const base = (o: string): string => (o === origin ? "" : o);

/** Where `GET /v1/me` and the other global routes go (WEB-SRV-BOOT-02), or undefined to keep `apiBase`. */
export function globalApiBase(): string | undefined {
  return discovery ? base(discovery.globalApi) : undefined;
}

/** Where the organisation's routes go: its region's `api` (WEB-SRV-BOOT-03), or undefined to keep `apiBase`. */
export function orgApiBase(orgId: string | undefined): string | undefined {
  const r = regionOfOrg(orgId);
  return r ? base(r.api) : undefined;
}

/**
 * The address to move to when the organisation's region serves the web app on another origin (WEB-SRV-BOOT-04,
 * REG-22): the same path there. Undefined when this is already the right origin or nothing is known.
 */
export function regionMoveTarget(orgId: string | undefined, here: Location = window.location): string | undefined {
  const r = regionOfOrg(orgId);
  if (!orgId || !r || r.web === origin) return undefined;
  // The organisation goes along: the remembered one is kept per origin, so the other origin would not know it.
  const to = new URL(here.pathname + here.search + here.hash, r.web);
  to.searchParams.set("org", orgId);
  return to.href;
}
